import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
// api imports removed — not currently used
import PersonaAvatar from '../../components/PersonaAvatar'
import type { Message } from '../../lib/types'

interface VideoFile { name: string; size: number; modified: string; url: string }
interface VideoFolder { id: string; name: string; videos: VideoFile[] }
interface VideosResponse { folders: VideoFolder[]; unfiled: VideoFile[] }
interface ReviewNote { timestamp: string; seconds: number; text: string }

function formatTime(s: number): string {
  const total = Math.floor(s)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  return `${m}:${String(sec).padStart(2, '0')}`
}

function formatSize(bytes: number): string {
  return bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`
}

function renderMarkdown(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(255,255,255,0.1);padding:1px 4px;border-radius:3px;font-size:12px">$1</code>')
    .replace(/\n/g, '<br/>')
}

export default function VideoReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const videoParam = searchParams.get('video')
  const [folders, setFolders] = useState<VideoFolder[]>([])
  const [unfiled, setUnfiled] = useState<VideoFile[]>([])
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [selectedVideo, setSelectedVideo] = useState<string | null>(videoParam)
  const [videoName, setVideoName] = useState(videoParam?.split('/').pop() || '')
  const [notes, setNotes] = useState<ReviewNote[]>([])
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [autoPause, setAutoPause] = useState(true)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [toast, setToast] = useState('')
  const [showVideoSwitcher, setShowVideoSwitcher] = useState(false)
  const [switcherExpanded, setSwitcherExpanded] = useState<Set<string>>(new Set())
  const [lastModified, setLastModified] = useState('')
  const [chatPersona, setChatPersona] = useState<string | null>(() => localStorage.getItem('vr_chat_persona'))
  const [chatConvId, setChatConvId] = useState<string | null>(() => localStorage.getItem('vr_chat_conv_id'))
  const [chatDraft, setChatDraft] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const chatInputRef = useRef<HTMLInputElement>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const scrubberRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [personas, setPersonas] = useState<string[]>([])
  useEffect(() => {
    const token = localStorage.getItem('session_token')
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
    const isCommunity = user?.userType === 'community'
    const communityId = user?.communityId || `portal-${user?.communitySlug || ''}`
    const url = isCommunity && communityId ? `/api/communities/${communityId}/personas` : '/api/personas'
    fetch(url, { credentials: 'include', headers }).then(r => r.json())
      .then((data: any) => {
        const names: string[] = data.personas || (Array.isArray(data) ? data.map((p: any) => p.name || p) : [])
        if (names.length > 0) setPersonas(names.sort())
      }).catch(() => {})
  }, [user])

  const isCommunity = user?.userType === 'community'
  const communityId = user?.communityId || ''
  const loadVideos = useCallback(() => {
    const params = isCommunity && communityId ? `?community=${encodeURIComponent(communityId)}` : ''
    fetch(`/api/videos${params}`).then(r => r.json()).then((d: VideosResponse) => {
      if (d.folders) setFolders(d.folders)
      if (d.unfiled) setUnfiled(d.unfiled)
    }).catch(() => {})
  }, [isCommunity, communityId])

  useEffect(() => { loadVideos() }, [loadVideos])

  const toggleFolder = useCallback((id: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const createFolder = useCallback(async () => {
    if (!newFolderName.trim()) return
    await fetch('/api/videos/folders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newFolderName.trim() }),
    })
    setNewFolderName('')
    setCreatingFolder(false)
    loadVideos()
  }, [newFolderName, loadVideos])

  useEffect(() => {
    if (!selectedVideo) return
    const saved = localStorage.getItem('vr_notes_' + selectedVideo)
    if (saved) try { setNotes(JSON.parse(saved)) } catch { setNotes([]) }
    else setNotes([])
  }, [selectedVideo])

  useEffect(() => {
    if (selectedVideo && notes.length > 0) localStorage.setItem('vr_notes_' + selectedVideo, JSON.stringify(notes))
  }, [notes, selectedVideo])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3000)
    return () => clearTimeout(t)
  }, [toast])

  // Auto-refresh: poll for video file changes every 5 seconds
  useEffect(() => {
    if (!selectedVideo || selectedVideo.startsWith('blob:')) return
    const check = async () => {
      try {
        const r = await fetch(selectedVideo, { method: 'HEAD' })
        const mod = r.headers.get('last-modified') || ''
        if (lastModified && mod && mod !== lastModified) {
          if (videoRef.current) {
            const t = videoRef.current.currentTime
            videoRef.current.src = selectedVideo + '?t=' + Date.now()
            videoRef.current.currentTime = t
            setToast('Video updated — reloaded')
          }
        }
        if (mod) setLastModified(mod)
      } catch {}
    }
    check()
    const interval = setInterval(check, 5000)
    return () => clearInterval(interval)
  }, [selectedVideo, lastModified])

  const selectVideo = useCallback((url: string, name: string) => {
    setSelectedVideo(url)
    setVideoName(name)
    setSearchParams({ video: url })
  }, [setSearchParams])

  const handleFileDrop = useCallback((files: FileList) => {
    const file = files[0]
    if (!file?.type.startsWith('video/')) return
    setSelectedVideo(URL.createObjectURL(file))
    setVideoName(file.name)
    setNotes([])
  }, [])

  const addNote = useCallback(() => {
    const text = inputRef.current?.value.trim()
    if (!text) return
    const ts = formatTime(currentTime)
    setNotes(prev => [...prev, { timestamp: ts, seconds: currentTime, text }])
    if (inputRef.current) inputRef.current.value = ''
    setToast(`Note added at ${ts}`)
    if (autoPause && videoRef.current?.paused) videoRef.current.play()
  }, [currentTime, autoPause])

  const copyNotes = useCallback(() => {
    const text = notes.map(n => `[${n.timestamp}] ${n.text}`).join('\n')
    navigator.clipboard.writeText(text).then(() => setToast('Copied to clipboard'))
  }, [notes])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (document.activeElement === inputRef.current || document.activeElement === chatInputRef.current) return
      if (!selectedVideo || !videoRef.current) return
      const v = videoRef.current
      switch (e.code) {
        case 'Space': e.preventDefault(); v.paused ? v.play() : v.pause(); break
        case 'ArrowLeft': e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - (e.shiftKey ? 10 : 5)); break
        case 'ArrowRight': e.preventDefault(); v.currentTime = Math.min(v.duration, v.currentTime + (e.shiftKey ? 10 : 5)); break
        case 'Enter': e.preventDefault(); inputRef.current?.focus(); break
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [selectedVideo])

  const changeSpeed = useCallback((rate: number) => {
    setPlaybackRate(rate)
    if (videoRef.current) videoRef.current.playbackRate = rate
  }, [])

  const scrubToX = useCallback((clientX: number) => {
    if (!scrubberRef.current || !videoRef.current) return
    const rect = scrubberRef.current.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    videoRef.current.currentTime = pct * duration
    setCurrentTime(pct * duration)
  }, [duration])

  const handleScrubberDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    scrubToX(e.clientX)
    const wasPlaying = videoRef.current && !videoRef.current.paused
    if (wasPlaying && videoRef.current) videoRef.current.pause()

    const onMove = (ev: MouseEvent) => { scrubToX(ev.clientX) }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      if (wasPlaying && videoRef.current) videoRef.current.play()
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [scrubToX])

  // Chat: open 1:1 with selected persona — uses Mini proxy for persistence
  const openChat = useCallback(async (persona: string) => {
    setChatPersona(persona)
    localStorage.setItem('vr_chat_persona', persona)
    setChatLoading(true)
    setChatConvId(null)
    localStorage.removeItem('vr_chat_conv_id')
    const currentUser = user?.loginName || user?.userName || 'joel'
    try {
      const res = await fetch('/api/vr-chat/conversation', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participants: [currentUser, persona], find_or_create: true }),
      })
      const data = await res.json()
      const convId = data?.id || data?.conversation?.id
      if (convId) {
        setChatConvId(convId)
        localStorage.setItem('vr_chat_conv_id', convId)
      }
    } catch { /* */ }
    setChatLoading(false)
  }, [user])

  // Auto-open persisted persona on mount — reuse saved conv ID if available
  useEffect(() => {
    const savedPersona = localStorage.getItem('vr_chat_persona')
    const savedConvId = localStorage.getItem('vr_chat_conv_id')
    if (savedPersona && savedConvId && !chatLoading) {
      setChatPersona(savedPersona)
      setChatConvId(savedConvId)
    } else if (savedPersona && !chatConvId && !chatLoading) {
      openChat(savedPersona)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: chatMessages } = useQuery({
    queryKey: ['chat-messages', chatConvId],
    queryFn: async () => {
      const res = await fetch(`/api/vr-chat/messages/${chatConvId}`, { credentials: 'include' })
      const data = await res.json()
      const msgs = Array.isArray(data) ? data : (data.messages || [])
      for (const m of msgs) {
        const raw = m as Record<string, unknown>
        if (!m.sender && (raw.sender_name || raw.author_name)) m.sender = String(raw.sender_name || raw.author_name)
      }
      return { messages: msgs }
    },
    enabled: !!chatConvId,
    refetchInterval: 5000,
  })

  const sendChatMut = useMutation({
    mutationFn: async (content: string) => {
      await fetch(`/api/vr-chat/messages/${chatConvId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, sender: user?.userId || user?.loginName || 'joel' }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', chatConvId] })
      setChatDraft('')
    },
  })

  useEffect(() => {
    if (chatEndRef.current?.parentElement) {
      const container = chatEndRef.current.parentElement
      container.scrollTop = container.scrollHeight
    }
  }, [chatMessages])

  const chatMsgs = chatMessages?.messages || []

  const VideoCard = ({ v }: { v: VideoFile }) => (
    <button
      onClick={() => selectVideo(v.url, v.name)}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
        padding: '10px 14px', textAlign: 'left', cursor: 'pointer', transition: 'border-color 0.15s', width: '100%',
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
    >
      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)', wordBreak: 'break-all' }}>{v.name}</div>
      <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
        {formatSize(v.size)} &middot; {new Date(v.modified).toLocaleDateString()}
      </div>
    </button>
  )

  if (!selectedVideo) {
    return (
      <div className="p-6" style={{ maxWidth: 960, margin: '0 auto' }}>
        <Link to="/tech" className="text-xs no-underline" style={{ color: 'var(--text-secondary)' }}>← Tech</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, marginBottom: 16 }}>
          <div>
            <h1 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Video Review</h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Select a video to review, or drag and drop a file.</p>
          </div>
          <button
            onClick={() => setCreatingFolder(true)}
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            + New Folder
          </button>
        </div>

        {creatingFolder && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              autoFocus
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setCreatingFolder(false) }}
              placeholder="Folder name..."
              style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', color: 'var(--text-primary)', fontSize: 14, outline: 'none' }}
            />
            <button onClick={createFolder} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>Create</button>
            <button onClick={() => setCreatingFolder(false)} style={{ background: 'var(--surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          </div>
        )}

        <div
          className="mb-6 p-4 text-center rounded-lg cursor-pointer"
          style={{ border: '2px dashed var(--border)', background: 'var(--surface)' }}
          onClick={() => {
            const input = document.createElement('input')
            input.type = 'file'; input.accept = 'video/*'
            input.onchange = () => { if (input.files?.length) handleFileDrop(input.files) }
            input.click()
          }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); if (e.dataTransfer.files.length) handleFileDrop(e.dataTransfer.files) }}
        >
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Drop a video here or click to upload from your computer</p>
        </div>

        {folders.map(folder => (
          <div key={folder.id} style={{ marginBottom: 16 }}>
            <button
              onClick={() => toggleFolder(folder.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0',
              }}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, transition: 'transform 0.15s', transform: expandedFolders.has(folder.id) ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{folder.name}</span>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{folder.videos.length} video{folder.videos.length !== 1 ? 's' : ''}</span>
            </button>
            {expandedFolders.has(folder.id) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8, paddingLeft: 20, marginTop: 4 }}>
                {folder.videos.map(v => <VideoCard key={v.name} v={v} />)}
              </div>
            )}
          </div>
        ))}

        {unfiled.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button
              onClick={() => toggleFolder('__unfiled')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0',
              }}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, transition: 'transform 0.15s', transform: expandedFolders.has('__unfiled') ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>Unfiled</span>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{unfiled.length} video{unfiled.length !== 1 ? 's' : ''}</span>
            </button>
            {expandedFolders.has('__unfiled') && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8, paddingLeft: 20, marginTop: 4 }}>
                {unfiled.map(v => <VideoCard key={v.name} v={v} />)}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const isSelfMsg = (msg: Message) => {
    const s = msg.sender?.toLowerCase()
    return s === user?.loginName?.toLowerCase() || s === user?.userName?.toLowerCase() || s === user?.userId?.toLowerCase() || s === 'joel'
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'fixed', top: 44, left: 0, right: 0, bottom: 0, zIndex: 10, background: 'var(--bg, #0d0d1a)' }}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* LEFT: Chat Panel */}
        <div style={{ width: 320, minWidth: 280, display: 'flex', flexDirection: 'column', background: 'var(--surface)', borderRight: '1px solid var(--border)', minHeight: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <select
              value={chatPersona || ''}
              onChange={e => { if (e.target.value) openChat(e.target.value) }}
              style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer' }}
            >
              <option value="">Select a persona to chat...</option>
              {personas.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
            {chatPersona && <PersonaAvatar name={chatPersona} size={28} />}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', minHeight: 0 }}>
            {!chatPersona && (
              <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-secondary)', fontSize: 13, fontStyle: 'italic' }}>
                Pick a persona to start a conversation while you review.
              </div>
            )}
            {chatLoading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>}
            {chatConvId && chatMsgs.map((msg: Message, i: number) => (
              <div key={i} style={{ padding: '6px 16px', display: 'flex', flexDirection: 'column', alignItems: isSelfMsg(msg) ? 'flex-end' : 'flex-start' }}>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2 }}>{msg.sender}</div>
                <div style={{
                  background: isSelfMsg(msg) ? 'rgba(89,197,250,0.15)' : 'rgba(255,255,255,0.06)',
                  borderRadius: 10,
                  padding: '8px 12px',
                  maxWidth: '85%',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--text-primary)',
                  wordBreak: 'break-word',
                }}>
                  <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content || '') }} />
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {chatConvId && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexShrink: 0 }}>
              <input
                ref={chatInputRef}
                value={chatDraft}
                onChange={e => setChatDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && chatDraft.trim()) { e.preventDefault(); sendChatMut.mutate(chatDraft.trim()) } }}
                placeholder={`Message ${chatPersona}...`}
                style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}
              />
              <button
                onClick={() => { if (chatDraft.trim()) sendChatMut.mutate(chatDraft.trim()) }}
                style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
              >Send</button>
            </div>
          )}
        </div>

        {/* CENTER: Video */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#000', minWidth: 0 }}>
          <div style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#FFD700' }}>{videoName}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <video
              ref={videoRef}
              src={selectedVideo}
              preload="auto"
              style={{ maxWidth: '100%', maxHeight: '100%', width: '100%' }}
              onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
              onLoadedMetadata={() => { setDuration(videoRef.current?.duration || 0); if (videoRef.current) videoRef.current.playbackRate = playbackRate }}
            />
          </div>
        </div>

        {/* RIGHT: Notes Panel */}
        <div style={{ width: 340, minWidth: 280, display: 'flex', flexDirection: 'column', background: 'var(--surface)', borderLeft: '1px solid var(--border)', minHeight: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)', letterSpacing: 1 }}>REVIEW NOTES</div>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{notes.length} note{notes.length !== 1 ? 's' : ''}</div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={autoPause} onChange={e => setAutoPause(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
              Auto-pause
            </label>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', minHeight: 0 }}>
            {notes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-secondary)', fontSize: 13, fontStyle: 'italic' }}>
                Play the video and type notes.
              </div>
            )}
            {notes.map((note, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '6px 16px' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <span onClick={() => { if (videoRef.current) { videoRef.current.currentTime = note.seconds; videoRef.current.play() } }}
                  style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)', cursor: 'pointer', whiteSpace: 'nowrap', padding: '2px 5px', borderRadius: 4, background: 'rgba(255,215,0,0.08)', flexShrink: 0 }}>
                  {note.timestamp}
                </span>
                <span style={{ flex: 1, fontSize: 12, lineHeight: 1.4, color: 'var(--text-primary)', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>{note.text}</span>
                <span onClick={() => setNotes(prev => prev.filter((_, j) => j !== i))}
                  style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, opacity: 0.3, flexShrink: 0 }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '0.3')}>&times;</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <input ref={inputRef} type="text" placeholder="Type a note..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote() } }}
                onFocus={() => { if (autoPause && videoRef.current && !videoRef.current.paused) videoRef.current.pause() }}
                style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
              <button onClick={addNote}
                style={{ background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.3)', color: 'var(--accent)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 14 }}>+</button>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={copyNotes} disabled={!notes.length}
                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.08)', color: 'var(--text-primary)', cursor: notes.length ? 'pointer' : 'default', fontSize: 12, fontWeight: 600, opacity: notes.length ? 1 : 0.4 }}>
                Copy All
              </button>
              {chatPersona && chatConvId && (
                <button
                  disabled={!notes.length}
                  onClick={async () => {
                    if (!notes.length || !chatConvId) return
                    const formatted = notes.map(n => `**[${n.timestamp}]** ${n.text}`).join('\n')
                    const msg = `📝 **Video Review Notes** — ${videoName}\n\n${formatted}`
                    sendChatMut.mutate(msg)
                    setToast(`Sent ${notes.length} notes to ${chatPersona}`)
                  }}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid rgba(255,215,0,0.3)', background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,180,0,0.15))', color: 'var(--accent)', cursor: notes.length ? 'pointer' : 'default', fontSize: 12, fontWeight: 600, opacity: notes.length ? 1 : 0.4 }}>
                  Send to {chatPersona.charAt(0).toUpperCase() + chatPersona.slice(1)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM: Scrubber + Controls */}
      <div style={{ background: '#12122a', borderTop: '1px solid rgba(255,215,0,0.15)', padding: '0 16px', paddingBottom: 12, flexShrink: 0, minHeight: 70 }}>
        {/* Scrubber bar */}
        <div
          ref={scrubberRef}
          onMouseDown={handleScrubberDown}
          style={{ height: 20, background: 'transparent', cursor: 'pointer', position: 'relative', margin: '4px 0', display: 'flex', alignItems: 'center', userSelect: 'none' }}
        >
          <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
            <div style={{
              height: '100%',
              width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
              background: 'linear-gradient(90deg, #FFD700, #FFA500)',
              borderRadius: 3,
            }} />
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
            transform: 'translate(-50%, -50%)',
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#FFD700',
            boxShadow: '0 0 8px rgba(255,215,0,0.5)',
            pointerEvents: 'none',
          }} />
        </div>

        {/* Controls row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8, fontSize: 13 }}>
          <button onClick={() => { if (videoRef.current) videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause() }}
            style={{ background: 'none', border: 'none', color: '#FFD700', cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>
            {videoRef.current?.paused !== false ? '▶' : '⏸'}
          </button>
          <span style={{ color: '#FFD700', fontFamily: 'monospace', fontSize: 13, flexShrink: 0 }}>{formatTime(currentTime)}</span>
          <span style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>/</span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace', fontSize: 13, flexShrink: 0 }}>{formatTime(duration)}</span>

          <button onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10) }}
            style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.2)', color: '#FFD700', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>-10s</button>
          <button onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 10) }}
            style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.2)', color: '#FFD700', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>+10s</button>

          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {[1, 1.25, 1.5, 2].map(rate => (
              <button
                key={rate}
                onClick={() => changeSpeed(rate)}
                style={{
                  background: playbackRate === rate ? '#FFD700' : 'rgba(255,215,0,0.1)',
                  border: `1px solid ${playbackRate === rate ? '#FFD700' : 'rgba(255,215,0,0.2)'}`,
                  color: playbackRate === rate ? '#000' : '#FFD700',
                  borderRadius: 4,
                  padding: '2px 8px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: playbackRate === rate ? 700 : 400,
                }}
              >
                {rate}x
              </button>
            ))}
          </div>

          {/* Videos button — OUTSIDE overflow container */}
          <div style={{ marginLeft: 'auto', position: 'relative', flexShrink: 0 }}>
            <button onClick={() => { setShowVideoSwitcher(v => !v); loadVideos() }}
              style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,180,0,0.15))', border: '1px solid rgba(255,215,0,0.3)', color: '#FFD700', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              🎬 Videos
            </button>
            {showVideoSwitcher && (
              <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: 8, width: 320, maxHeight: 400, overflowY: 'auto', background: 'var(--surface, #1a1a2e)', border: '1px solid var(--border, #333)', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 50 }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border, #333)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary, #eee)' }}>Switch Video</span>
                  <button onClick={() => { setSelectedVideo(null); setSearchParams({}); setShowVideoSwitcher(false) }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #888)', cursor: 'pointer', fontSize: 11 }}>Browse All →</button>
                </div>
                {folders.map(f => {
                  const isOpen = switcherExpanded.has(f.id)
                  return (
                  <div key={f.id}>
                    <button
                      onClick={() => setSwitcherExpanded(prev => { const next = new Set(prev); next.has(f.id) ? next.delete(f.id) : next.add(f.id); return next })}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#FFD700', background: 'rgba(255,215,0,0.05)', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <span style={{ fontSize: 10, transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      {f.name}
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,215,0,0.5)', fontWeight: 400 }}>{f.videos.length}</span>
                    </button>
                    {isOpen && f.videos.map(v => (
                      <button key={v.url} onClick={() => { selectVideo(v.url, v.name); setShowVideoSwitcher(false) }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px 6px 28px', background: v.url === selectedVideo ? 'rgba(255,215,0,0.1)' : 'none', border: 'none', color: v.url === selectedVideo ? '#FFD700' : 'var(--text-primary, #ccc)', cursor: 'pointer', fontSize: 12 }}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                  )
                })}
                {unfiled.length > 0 && (
                  <div>
                    <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary, #888)', background: 'rgba(255,255,255,0.03)' }}>Unfiled</div>
                    {unfiled.map(v => (
                      <button key={v.url} onClick={() => { selectVideo(v.url, v.name); setShowVideoSwitcher(false) }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px 6px 24px', background: v.url === selectedVideo ? 'rgba(255,215,0,0.1)' : 'none', border: 'none', color: v.url === selectedVideo ? '#FFD700' : 'var(--text-primary, #ccc)', cursor: 'pointer', fontSize: 12 }}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--surface)', border: '1px solid var(--accent)', color: 'var(--accent)',
          padding: '10px 24px', borderRadius: 8, fontSize: 13, zIndex: 100, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        }}>{toast}</div>
      )}
    </div>
  )
}
